import { useState, useCallback, useRef, useEffect, memo, type ReactNode, type MouseEvent as ReactMouseEvent, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useDeleteFolderMutation, useDeleteFolderWithConnectionsMutation, useUpdateFolderMutation, type FolderResponse } from '@/hooks/useDatasourceFolders'
import { useDatasourceFolderStore } from '@/stores/datasourceFolderStore'
import {
  FolderIcon,
  FolderOpenIcon,
  FolderPlusIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  PencilIcon,
  TrashIcon,
  Bars2Icon,
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import { notify } from '@/utils/notify'

interface FolderRowProps {
  folder: FolderResponse
  children?: ReactNode
  depth?: number
  onCreateSubfolder?: (parentFolderId: string) => void
  forceExpanded?: boolean
  isHighlighted?: boolean
  connectionIds?: string[]
  subfolderIds?: string[]
  subfolderConnectionIds?: string[]
  isDropTarget?: boolean
  disabled?: boolean
}

export const FolderRow = memo(function FolderRow({
  folder,
  children,
  depth = 0,
  onCreateSubfolder,
  forceExpanded = false,
  isHighlighted = false,
  connectionIds = [],
  subfolderIds = [],
  subfolderConnectionIds = [],
  isDropTarget = false,
  disabled = false,
}: FolderRowProps) {
  const { isFolderExpanded, toggleFolder } = useDatasourceFolderStore()
  const deleteFolderMutation = useDeleteFolderMutation()
  const deleteFolderWithConnectionsMutation = useDeleteFolderWithConnectionsMutation()
  const updateFolderMutation = useUpdateFolderMutation()

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteMode, setDeleteMode] = useState<'move' | 'delete'>('move')
  const [isEditing, setIsEditing] = useState(false)
  const [editName, setEditName] = useState(folder.name || '')
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const contextMenuRef = useRef<HTMLDivElement>(null)

  // Use forceExpanded during search, otherwise use store state
  const isExpanded = forceExpanded || (folder.id ? isFolderExpanded(folder.id) : false)

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  // Close context menu when clicking outside
  useEffect(() => {
    if (!contextMenu) return

    const handleClickOutside = (e: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
        setContextMenu(null)
      }
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setContextMenu(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [contextMenu])

  // Handle right-click context menu
  const handleContextMenu = useCallback((e: ReactMouseEvent) => {
    e.preventDefault()
    setContextMenu({ x: e.clientX, y: e.clientY })
  }, [])

  // Close context menu
  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null)
  }, [])

  // @dnd-kit sortable setup for smooth reordering animations
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `ds-folder::${folder.id}`,
    disabled: disabled || !folder.id,
    data: {
      type: 'folder' as const,
      folderId: folder.id,
      parentFolderId: folder.parentFolderId || null,
      folder,
    },
  })

  const sortableStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
    zIndex: isDragging ? 50 : undefined,
    position: 'relative' as const,
  }

  const handleToggle = useCallback(() => {
    if (folder.id) {
      toggleFolder(folder.id)
    }
  }, [folder.id, toggleFolder])

  const handleStartEdit = useCallback(() => {
    setEditName(folder.name || '')
    setIsEditing(true)
  }, [folder.name])

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false)
    setEditName(folder.name || '')
  }, [folder.name])

  const handleSaveEdit = useCallback(async () => {
    if (!folder.id || !editName.trim()) {
      handleCancelEdit()
      return
    }

    if (editName.trim() === folder.name) {
      setIsEditing(false)
      return
    }

    try {
      await updateFolderMutation.mutateAsync({
        folderId: folder.id,
        data: { name: editName.trim() },
      })
      setIsEditing(false)
      notify.success('Folder renamed')
    } catch {
      notify.error('Failed to rename folder')
    }
  }, [folder.id, folder.name, editName, updateFolderMutation, handleCancelEdit])

  const handleKeyDown = useCallback(
    (e: ReactKeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        void handleSaveEdit()
      } else if (e.key === 'Escape') {
        handleCancelEdit()
      }
    },
    [handleSaveEdit, handleCancelEdit]
  )

  // Calculate total items to be deleted
  const totalConnections = connectionIds.length + subfolderConnectionIds.length
  // +1 for the current folder
  const totalFolders = subfolderIds.length + 1

  const handleDeleteConfirm = useCallback(async () => {
    if (!folder.id) return

    try {
      if (deleteMode === 'delete' && (totalConnections > 0 || subfolderIds.length > 0)) {
        await deleteFolderWithConnectionsMutation.mutateAsync({
          folderId: folder.id,
          connectionIds,
          subfolderIds,
          subfolderConnectionIds,
        })
        const parts: string[] = []
        if (totalFolders > 1) {
          parts.push(`${totalFolders} folder(s)`)
        } else {
          parts.push('Folder')
        }
        if (totalConnections > 0) {
          parts.push(`${totalConnections} connection(s)`)
        }
        notify.success(`${parts.join(' and ')} deleted`)
      } else {
        await deleteFolderMutation.mutateAsync(folder.id)
        notify.success('Folder deleted')
      }
      // Close modal after successful deletion
      setShowDeleteConfirm(false)
      setDeleteMode('move')
    } catch {
      notify.error('Failed to delete folder')
    }
  }, [folder.id, deleteMode, connectionIds, subfolderIds, subfolderConnectionIds, totalConnections, totalFolders, deleteFolderMutation, deleteFolderWithConnectionsMutation])

  const handleCloseDeleteConfirm = useCallback(() => {
    setShowDeleteConfirm(false)
    setDeleteMode('move')
  }, [])

  const handleConfirmDelete = useCallback(() => {
    void handleDeleteConfirm()
  }, [handleDeleteConfirm])

  const isDatasourceDrop = isDropTarget

  // Handle row click to expand/collapse (but not when clicking on interactive elements)
  const handleRowClick = useCallback((e: ReactMouseEvent) => {
    // Do not toggle if clicking on buttons, inputs, or the drag handle
    const target = e.target as HTMLElement
    if (
      target.closest('button') ||
      target.closest('input') ||
      target.closest('[data-drag-handle]')
    ) {
      return
    }
    handleToggle()
  }, [handleToggle])

  return (
    <div>
      {/* Sortable wrapper: encompasses folder header + children so they move together during drag */}
      <div ref={setNodeRef} style={sortableStyle} className={clsx(
          'space-y-1',
          isDragging && 'rounded-xl border-2 border-dashed border-amber-400/50 dark:border-amber-500/30'
        )}>
        <div
          onClick={handleRowClick}
          onContextMenu={handleContextMenu}
          className={clsx(
            'flex items-center gap-3 px-4 py-1.5 rounded-lg transition-all duration-200 cursor-pointer',
            'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800',
            'hover:bg-blue-100 dark:hover:bg-blue-900/30',
            isDatasourceDrop && 'ring-2 ring-green-500 ring-offset-2 bg-green-50 dark:bg-green-900/20',
            isHighlighted && 'ring-2 ring-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
          )}
        >
          {/* Drag Handle Indicator - listeners/attributes here so only the handle triggers drag */}
          <div
            data-drag-handle
            {...attributes}
            {...listeners}
            className={clsx(
              'flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300',
              !disabled && 'cursor-grab active:cursor-grabbing'
            )}
          >
            <Bars2Icon className="w-5 h-5" />
          </div>

          {/* Expand/Collapse Toggle */}
          <button
            type="button"
            onClick={handleToggle}
            className="flex-shrink-0 p-0.5 hover:bg-blue-200 dark:hover:bg-blue-800 rounded transition-colors"
          >
            {isExpanded ? (
              <ChevronDownIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            ) : (
              <ChevronRightIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            )}
          </button>

          {/* Folder Icon */}
          <div className="flex-shrink-0">
            {isExpanded ? (
              <FolderOpenIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            ) : (
              <FolderIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            )}
          </div>

          {/* Folder Name */}
          <div className="flex-1 min-w-0">
            {isEditing ? (
              <input
                ref={inputRef}
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onBlur={() => void handleSaveEdit()}
                onKeyDown={handleKeyDown}
                className="w-full px-2 py-0.5 text-sm font-medium bg-white dark:bg-gray-700 border border-blue-400 dark:border-blue-500 rounded focus:outline-none focus:border-gray-500 dark:focus:border-gray-400 focus:ring-0"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span
                className="text-sm font-medium text-gray-900 dark:text-white"
                onDoubleClick={handleStartEdit}
              >
                {folder.name}
              </span>
            )}
          </div>

          {/* Datasource Count Badge */}
          {(folder.datasourceCount ?? 0) > 0 && (
            <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium bg-blue-200 dark:bg-blue-800 text-blue-700 dark:text-blue-300 rounded-full">
              {folder.datasourceCount}
            </span>
          )}

          {/* Drop indicator text */}
          {isDatasourceDrop && (
            <span className="flex-shrink-0 text-xs text-green-600 dark:text-green-400 font-medium">
              Drop here
            </span>
          )}

          {/* Action Buttons */}
          {!isEditing && (
            <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                type="button"
                onClick={handleStartEdit}
                className="p-1.5 rounded hover:bg-blue-200 dark:hover:bg-blue-800 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                title="Rename folder"
                aria-label={`Rename folder ${folder.name}`}
              >
                <PencilIcon className="w-4 h-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleteFolderMutation.isPending}
                className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 transition-colors"
                title="Delete folder"
                aria-label={`Delete folder ${folder.name}`}
              >
                <TrashIcon className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>

        {/* Children (datasources in this folder) - inside sortable wrapper so they move with folder */}
        {/* Hide children during drag to create a clean compact placeholder at the source */}
        {isExpanded && !isDragging && children && <div className="space-y-1 ml-8">{children}</div>}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          ref={contextMenuRef}
          className="fixed z-50 min-w-[180px] py-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          {/* Only show Create Subfolder for root folders (depth 0) */}
          {depth === 0 && onCreateSubfolder && folder.id && (
            <button
              type="button"
              onClick={() => {
                handleCloseContextMenu()
                if (folder.id) {
                  onCreateSubfolder(folder.id)
                }
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <FolderPlusIcon className="w-4 h-4" />
              Create Subfolder
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              handleCloseContextMenu()
              handleStartEdit()
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <PencilIcon className="w-4 h-4" />
            Rename
          </button>
          <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
          <button
            type="button"
            onClick={() => {
              handleCloseContextMenu()
              setShowDeleteConfirm(true)
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            <TrashIcon className="w-4 h-4" />
            Delete
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
          onClick={handleCloseDeleteConfirm}
        >
          <div
            className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/20">
                  <TrashIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Delete Folder
                </h3>
              </div>
              <button
                type="button"
                onClick={handleCloseDeleteConfirm}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                <span className="sr-only">Close</span>
                ×
              </button>
            </div>

            {/* Body */}
            <div className="p-4">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                Are you sure you want to delete the folder "{folder.name}"?
              </p>

              {(totalConnections > 0 || subfolderIds.length > 0) && (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    {(() => {
                      const parts: string[] = []
                      if (subfolderIds.length > 0) {
                        parts.push(`${subfolderIds.length} subfolder(s)`)
                      }
                      if (totalConnections > 0) {
                        parts.push(`${totalConnections} connection(s)`)
                      }
                      return `This folder contains ${parts.join(' and ')}. What would you like to do?`
                    })()}
                  </p>
                  <div className="space-y-2">
                    <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <input
                        type="radio"
                        name="deleteMode"
                        value="move"
                        checked={deleteMode === 'move'}
                        onChange={() => setDeleteMode('move')}
                        className="mt-0.5"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">Move contents to root</span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {subfolderIds.length > 0
                            ? 'Subfolders will become root folders, connections will be moved to uncategorized'
                            : 'Connections will be moved to the uncategorized section'}
                        </p>
                      </div>
                    </label>
                    <label className="flex items-start gap-3 p-3 rounded-lg border border-red-200 dark:border-red-800 cursor-pointer hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                      <input
                        type="radio"
                        name="deleteMode"
                        value="delete"
                        checked={deleteMode === 'delete'}
                        onChange={() => setDeleteMode('delete')}
                        className="mt-0.5"
                      />
                      <div>
                        <span className="text-sm font-medium text-red-600 dark:text-red-400">Delete everything</span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {(() => {
                            const parts: string[] = []
                            if (subfolderIds.length > 0) {
                              parts.push(`${subfolderIds.length} subfolder(s)`)
                            }
                            if (totalConnections > 0) {
                              parts.push(`${totalConnections} connection(s)`)
                            }
                            return `All ${parts.join(' and ')} will be permanently deleted`
                          })()}
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end space-x-3 p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleCloseDeleteConfirm}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={deleteFolderMutation.isPending || deleteFolderWithConnectionsMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                {deleteFolderMutation.isPending || deleteFolderWithConnectionsMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
})
